// ─── src/pages/PublicationsPage.jsx ──────────────────────────────────────────
// Public luxury magazine landing page.
// Route: /publications
// Cinematic hero with latest cover + premium issue grid.

import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchIssues } from '../services/magazineIssuesService';
import { imgUrl } from '../utils/imgUrl';
import { supabase } from '../lib/supabaseClient';
import HomeNav from '../components/nav/HomeNav.jsx';
import { usePrerenderReady } from '../utils/prerenderReady';

const GOLD = '#d39a21';
const GD   = "var(--font-heading-primary, 'Cormorant Garamond', Georgia, serif)";
const NU   = "var(--font-body, 'Jost', sans-serif)";
const BG   = '#080706';

// ── Palette ───────────────────────────────────────────────────────────────────
// The page already received `darkMode` and used it for nothing but HomeNav, so it
// painted itself near-black whatever theme the reader had chosen. In light mode it
// now reads as a magazine: a cinematic dark cover, then cream inside-pages.
//
// The hero is deliberately excluded — it is a full-bleed photograph with its own
// gradient, so it stays dark in both themes and the cream begins below it.
//
// `gold` differs by theme because #d39a21 is a low-contrast wash on ivory; the
// light value is the same darker gold the site's own light tokens use. `onGold`
// is what sits ON a gold fill, which is the opposite surface in each theme.
function paletteFor(darkMode) {
  return darkMode
    ? {
        bg:        BG,
        surface:   '#1a1612',              // card + skeleton fill behind covers
        text:      '#ffffff',
        textSoft:  'rgba(255,255,255,0.55)',
        textFaint: 'rgba(255,255,255,0.40)',
        inputBg:   'rgba(255,255,255,0.03)',
        gold:      GOLD,
        hairline:  'rgba(211,154,33,0.25)',
        cardEdge:  'rgba(211,154,33,0.10)',
        cardEdgeHover: 'rgba(211,154,33,0.50)',
        onGold:    BG,
      }
    : {
        bg:        '#FBF7F4',
        surface:   '#F2F0EA',
        text:      '#1A1512',
        textSoft:  'rgba(26,21,18,0.60)',
        textFaint: 'rgba(26,21,18,0.45)',
        inputBg:   'rgba(26,21,18,0.03)',
        gold:      '#7a5f10',
        hairline:  'rgba(122,95,16,0.30)',
        cardEdge:  'rgba(122,95,16,0.18)',
        cardEdgeHover: 'rgba(122,95,16,0.55)',
        onGold:    '#FBF7F4',
      };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
// listings.country is inconsistent at the source in two distinct ways: casing
// ("Italy" vs "australia") AND real duplicate spellings for the same country
// ("usa" / "United States" / "united-states" all appear). This is display-only
// normalisation into one canonical key + label — it never writes back to the DB.
// Small connector words stay lowercase in multi-word names, except when first.
const COUNTRY_ALIASES = {
  usa: 'United States',
  'united states': 'United States',
  uae: 'United Arab Emirates',
  'united arab emirates': 'United Arab Emirates',
};
const COUNTRY_SMALL_WORDS = new Set(['and', 'of', 'the']);

function smartTitleCase(str) {
  const words = String(str).split(' ').filter(Boolean);
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && COUNTRY_SMALL_WORDS.has(lower)) return lower;
      return lower[0].toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

// Returns { key, label } — key is stable for de-duplication/matching, label is
// what renders on the pill.
function normalizeCountry(raw) {
  const spaced = String(raw).trim().replace(/-/g, ' ');
  const lower = spaced.toLowerCase();
  const label = COUNTRY_ALIASES[lower] || smartTitleCase(spaced);
  return { key: label.toLowerCase(), label };
}

function seasonLabel(issue) {
  const parts = [];
  if (issue.season) parts.push(issue.season);
  if (issue.year)   parts.push(issue.year);
  return parts.join(' ');
}

function issueLabel(issue) {
  const parts = [];
  if (issue.issue_number) parts.push(`Issue ${String(issue.issue_number).padStart(2, '0')}`);
  const sl = seasonLabel(issue);
  if (sl) parts.push(sl);
  return parts.join(' · ');
}

// ── Scroll-down chevron ───────────────────────────────────────────────────────
function ScrollChevron({ targetId }) {
  return (
    <button
      onClick={() => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }}
      style={{
        position:   'absolute',
        bottom:     32,
        left:       '50%',
        transform:  'translateX(-50%)',
        background: 'none',
        border:     'none',
        cursor:     'pointer',
        padding:    8,
        display:    'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap:        4,
        animation:  'lwdChevronBounce 2s ease-in-out infinite',
      }}
      aria-label="Scroll to all issues"
    >
      <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
        <path d="M1 1L10 10L19 1" stroke="rgba(211,154,33,0.6)" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <style>{`
        @keyframes lwdChevronBounce {
          0%, 100% { transform: translateX(-50%) translateY(0); opacity: 0.6; }
          50%       { transform: translateX(-50%) translateY(6px); opacity: 1; }
        }
      `}</style>
    </button>
  );
}

// ── Back link ─────────────────────────────────────────────────────────────────
function BackLink({ onBack, P }) {
  // Fixed to the viewport, so it starts over the dark hero and ends over the page
  // body. White reads on both in dark mode; in light mode it would vanish the
  // moment the reader scrolls past the hero, so it takes the body's ink instead.
  const rest = P.text === '#ffffff' ? 'rgba(255,255,255,0.7)' : P.textSoft;
  return (
    <button
      onClick={() => {
        if (onBack) onBack();
        else window.history.back();
      }}
      style={{
        position:      'fixed',
        top:           24,
        left:          24,
        zIndex:        100,
        background:    'none',
        border:        'none',
        cursor:        'pointer',
        display:       'flex',
        alignItems:    'center',
        gap:           8,
        fontFamily:    NU,
        fontSize:      11,
        fontWeight:    500,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color:         rest,
        padding:       '6px 0',
        transition:    'color 0.2s',
      }}
      onMouseEnter={e => e.currentTarget.style.color = P.gold}
      onMouseLeave={e => e.currentTarget.style.color = rest}
    >
      ← <span>LWD</span>
    </button>
  );
}

// ── Hero section ──────────────────────────────────────────────────────────────
function Hero({ featured, onRead, isMobile }) {
  const hasImage = !!(featured && featured.cover_image);
  const label    = featured ? issueLabel(featured) : '';

  // Noise SVG pattern for when there is no cover image
  const noiseBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`;

  return (
    <div style={{
      position:   'relative',
      height:     '100vh',
      minHeight:  600,
      background: BG,
      overflow:   'hidden',
      display:    'flex',
      // Desktop: sit the headline in the lower third, like the title block on a
      // real magazine cover. Mobile stays vertically centred.
      alignItems: isMobile ? 'center' : 'flex-end',
    }}>
      {/* Background cover image (right side) */}
      {hasImage ? (
        <>
          <img
            src={imgUrl(featured.cover_image, { width: 1800, resize: 'contain' })}
            alt={featured.title || 'Latest issue'}
            style={{
              position:   'absolute',
              inset:      0,
              width:      '100%',
              height:     '100%',
              objectFit:  'cover',
              objectPosition: 'right center',
              opacity:    isMobile ? 0.18 : 1,
            }}
          />
          <div style={{
            position:   'absolute',
            inset:      0,
            background: isMobile
              ? `linear-gradient(to bottom, rgba(8,7,6,0.95) 0%, rgba(8,7,6,0.85) 100%)`
              : `linear-gradient(to right, rgba(8,7,6,0.97) 45%, rgba(8,7,6,0.3) 100%)`,
          }} />
        </>
      ) : (
        <div style={{
          position:   'absolute',
          inset:      0,
          backgroundImage: noiseBg,
          backgroundRepeat: 'repeat',
        }} />
      )}

      {/* Left-side content */}
      <div style={{
        position:  'relative',
        zIndex:    2,
        maxWidth:  isMobile ? '100%' : 690,
        padding:   isMobile ? '100px 24px 80px' : '0 0 clamp(72px, 12vh, 150px) clamp(40px, 7vw, 120px)',
      }}>
        {/* Eyebrow */}
        <div style={{
          fontFamily:    NU,
          fontSize:      9,
          fontWeight:    600,
          color:         GOLD,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          marginBottom:  16,
        }}>
          ✦&nbsp;&nbsp;The Magazine
        </div>

        {/* Issue label */}
        {label && (
          <div style={{
            fontFamily:    NU,
            fontSize:      11,
            color:         'rgba(255,255,255,0.45)',
            letterSpacing: '0.08em',
            marginBottom:  20,
          }}>
            {label}
          </div>
        )}

        {/* Title */}
        <h1 style={{
          fontFamily:    GD,
          fontSize:      'clamp(42px, 6vw, 78px)',
          fontWeight:    300,
          fontStyle:     'italic',
          color:         '#fff',
          margin:        '0 0 24px',
          letterSpacing: '0.02em',
          lineHeight:    1.04,
        }}>
          {featured ? (featured.title || 'The Magazine') : 'The Magazine'}
        </h1>

        {/* Excerpt */}
        {featured && featured.excerpt && (
          <p style={{
            fontFamily:    NU,
            fontSize:      15,
            color:         'rgba(255,255,255,0.6)',
            maxWidth:      480,
            lineHeight:    1.65,
            margin:        '0 0 36px',
            display:       '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow:      'hidden',
          }}>
            {featured.excerpt}
          </p>
        )}

        {!featured && (
          <p style={{
            fontFamily:    NU,
            fontSize:      15,
            color:         'rgba(255,255,255,0.6)',
            maxWidth:      480,
            lineHeight:    1.65,
            margin:        '0 0 36px',
          }}>
            Immersive editorial, curated for couples planning exceptional celebrations.
          </p>
        )}

        {/* CTA row */}
        <div style={{
          display:    'flex',
          alignItems: 'center',
          gap:        28,
          flexWrap:   'wrap',
        }}>
          {featured && onRead && (
            <a
              href={`/publications/${featured.slug}`}
              onClick={(e) => { e.preventDefault(); onRead(featured.slug); }}
              style={{
                display:         'inline-block',
                background:      GOLD,
                color:           '#080706',
                fontFamily:      NU,
                fontSize:        12,
                fontWeight:      600,
                letterSpacing:   '0.12em',
                textTransform:   'uppercase',
                padding:         '14px 32px',
                textDecoration:  'none',
                cursor:          'pointer',
                transition:      'opacity 0.2s, transform 0.2s',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'none'; }}
            >
              Read Now →
            </a>
          )}

          <button
            onClick={() => {
              const el = document.getElementById('all-issues');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              background:    'none',
              border:        'none',
              cursor:        'pointer',
              fontFamily:    NU,
              fontSize:      12,
              color:         'rgba(255,255,255,0.5)',
              letterSpacing: '0.08em',
              padding:       0,
              transition:    'color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.9)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
          >
            ↓ All Issues
          </button>
        </div>
      </div>

      {/* Scroll chevron */}
      <ScrollChevron targetId="all-issues" />
    </div>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard({ P }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{
        position:       'relative',
        paddingBottom:  '141.4%',
        background:     P.surface,
        animation:      'lwdSkeletonPulse 1.6s ease-in-out infinite',
      }} />
      <div style={{ padding: '14px 0 0' }}>
        <div style={{
          height:     10,
          width:      '50%',
          background: P.surface,
          marginBottom: 10,
          animation:  'lwdSkeletonPulse 1.6s ease-in-out infinite',
        }} />
        <div style={{
          height:     18,
          width:      '80%',
          background: P.surface,
          animation:  'lwdSkeletonPulse 1.6s ease-in-out infinite 0.2s',
        }} />
      </div>
      <style>{`
        @keyframes lwdSkeletonPulse {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

// ── Issue card ────────────────────────────────────────────────────────────────
function IssueCard({ issue, onRead, isLatest, P }) {
  const [hovered, setHovered] = useState(false);

  return (
    <a
      href={`/publications/${issue.slug}`}
      onClick={(e) => { e.preventDefault(); onRead && onRead(issue.slug); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor:          'pointer',
        display:         'flex',
        flexDirection:   'column',
        textDecoration:  'none',
        transition:      'transform 0.3s ease',
        transform:       hovered ? 'translateY(-6px)' : 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Cover wrapper, A4 ratio */}
      <div style={{
        position:      'relative',
        paddingBottom: '141.4%',
        background:    P.surface,
        border:        `1px solid ${hovered ? P.cardEdgeHover : P.cardEdge}`,
        transition:    'border-color 0.25s',
        overflow:      'hidden',
      }}>
        {issue.cover_image ? (
          <img
            src={imgUrl(issue.cover_image, { width: 800, resize: 'contain' })}
            alt={issue.title || 'Magazine cover'}
            loading={isLatest ? 'eager' : 'lazy'}
            decoding="async"
            style={{
              position:   'absolute',
              inset:      0,
              width:      '100%',
              height:     '100%',
              objectFit:  'cover',
              transition: 'transform 0.4s ease',
              transform:  hovered ? 'scale(1.04)' : 'scale(1)',
            }}
          />
        ) : (
          <div style={{
            position:       'absolute',
            inset:          0,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            flexDirection:  'column',
            gap:            10,
          }}>
            <span style={{ fontSize: 32, opacity: 0.15, color: P.gold }}>◈</span>
          </div>
        )}

        {/* Latest badge */}
        {isLatest && (
          <div style={{
            position:      'absolute',
            top:           12,
            left:          12,
            background:    GOLD,
            color:         '#080706',
            fontFamily:    NU,
            fontSize:      8,
            fontWeight:    700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            padding:       '4px 8px',
          }}>
            ✦ Latest
          </div>
        )}

        {/* Premium badge */}
        {issue.access_level === 'premium' && (
          <div style={{
            position:      'absolute',
            top:           12,
            right:         12,
            background:    'rgba(8,7,6,0.85)',
            border:        `1px solid ${GOLD}`,
            color:         GOLD,
            fontFamily:    NU,
            fontSize:      8,
            fontWeight:    600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            padding:       '4px 8px',
          }}>
            ✦ Premium
          </div>
        )}
      </div>

      {/* Info strip */}
      <div style={{ padding: '18px 0 0' }}>
        {/* Issue number + season row, only when there's an issue number or a
            named season. A bare year is removed (luxury archives don't label
            every cover with a date). */}
        {(issue.issue_number || issue.season) && (
          <div style={{
            display:       'flex',
            alignItems:    'center',
            gap:           8,
            marginBottom:  9,
          }}>
            {issue.issue_number && (
              <span style={{
                background:    P.gold,
                color:         P.onGold,
                fontFamily:    NU,
                fontSize:      8,
                fontWeight:    700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                padding:       '3px 7px',
              }}>
                Issue {issue.issue_number}
              </span>
            )}
            {issue.season && (
              <span style={{
                fontFamily:    NU,
                fontSize:      9,
                fontWeight:    600,
                color:         P.gold,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}>
                {issue.season}
              </span>
            )}
          </div>
        )}

        {/* Title */}
        <h3 style={{
          fontFamily:        GD,
          fontSize:          20,
          fontWeight:        400,
          fontStyle:         'italic',
          color:             P.text,
          margin:            '0 0 10px',
          letterSpacing:     '0.02em',
          lineHeight:        1.32,
          display:           '-webkit-box',
          WebkitLineClamp:   2,
          WebkitBoxOrient:   'vertical',
          overflow:          'hidden',
        }}>
          {issue.title || 'Untitled Issue'}
        </h3>

        {/* Read label, fades in on hover */}
        <div style={{
          fontFamily:    NU,
          fontSize:      10,
          fontWeight:    600,
          color:         P.gold,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          opacity:       hovered ? 1 : 0,
          transition:    'opacity 0.2s',
        }}>
          Read →
        </div>
      </div>
    </a>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, P }) {
  return (
    <div style={{
      display:    'flex',
      alignItems: 'center',
      gap:        24,
      marginBottom: 48,
    }}>
      <h2 style={{
        fontFamily:    GD,
        fontSize:      29,
        fontWeight:    300,
        fontStyle:     'italic',
        color:         P.text,
        margin:        0,
        letterSpacing: '0.02em',
        whiteSpace:    'nowrap',
      }}>
        {title}
      </h2>
      <div style={{
        flex:       1,
        height:     1,
        background: P.hairline,
      }} />
    </div>
  );
}

// ── Brand tabs ────────────────────────────────────────────────────────────────
// Directory = automatic per-venue/vendor covers (one per listing). Magazine =
// hand-curated Luxury Wedding Magazine editorial issues. Both live in the same
// `magazine_issues` table, distinguished by the `brand` column.
function BrandTabs({ active, onChange, counts, P }) {
  const tabs = [
    { key: 'directory', label: 'Directory', count: counts.directory },
    { key: 'magazine',  label: 'The Magazine', count: counts.magazine },
  ];
  return (
    <div style={{ display: 'flex', gap: 32, marginBottom: 28, borderBottom: `1px solid ${P.hairline}` }}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          style={{
            background:    'none',
            border:        'none',
            cursor:        'pointer',
            padding:       '0 0 14px',
            fontFamily:    GD,
            fontSize:      19,
            fontStyle:     'italic',
            fontWeight:    active === tab.key ? 500 : 300,
            letterSpacing: '0.02em',
            color:         active === tab.key ? P.text : P.textFaint,
            borderBottom:  `2px solid ${active === tab.key ? P.gold : 'transparent'}`,
            marginBottom:  -1,
            transition:    'color 0.2s, border-color 0.2s',
          }}
        >
          {tab.label}
          {typeof tab.count === 'number' && (
            <span style={{
              fontFamily:    NU,
              fontStyle:     'normal',
              fontSize:      11,
              fontWeight:    600,
              color:         P.textFaint,
              marginLeft:    8,
            }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Country filter ────────────────────────────────────────────────────────────
// Only meaningful for Directory issues, which join 1:1 to a listing (and so a
// country) by slug. Magazine editorial pieces aren't reliably single-country,
// so this row only renders on the Directory tab.
function CountryFilter({ countries, active, onChange, P, isMobile }) {
  if (countries.length <= 1) return null;
  return (
    <div style={{
      display:      'flex',
      alignItems:   isMobile ? 'center' : 'flex-start',
      gap:          12,
      marginBottom: 32,
    }}>
      <span style={{
        fontFamily:    NU,
        fontSize:      9,
        fontWeight:    700,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color:         P.textFaint,
        flexShrink:    0,
        borderRight:   `1px solid ${P.hairline}`,
        paddingRight:  12,
        paddingTop:    isMobile ? 0 : 6,
      }}>
        Country
      </span>
      {/* On mobile, wrapping 40+ pills runs to 8-10 rows before any real content
          shows. A single horizontal scroll strip (standard mobile filter-chip
          pattern) keeps it to one row; desktop keeps the full wrap so every
          country stays scannable at a glance. minWidth:0 lets this flex child
          actually shrink so overflowX can kick in instead of just growing past
          the row and pushing it wide. */}
      <div style={{
        display:       'flex',
        flexWrap:      isMobile ? 'nowrap' : 'wrap',
        gap:           6,
        minWidth:      0,
        overflowX:     isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: isMobile ? 'touch' : undefined,
        paddingBottom: isMobile ? 4 : 0,
      }}>
        {['All', ...countries].map(c => (
          <button
            key={c}
            onClick={() => onChange(c)}
            style={{
              flexShrink:    0,
              padding:       '5px 14px',
              borderRadius:  20,
              border:        `1px solid ${active === c ? P.gold : P.hairline}`,
              background:    active === c ? 'rgba(211,154,33,0.12)' : 'transparent',
              cursor:        'pointer',
              whiteSpace:    'nowrap',
              fontFamily:    NU,
              fontSize:      11,
              letterSpacing: '0.04em',
              color:         active === c ? P.gold : P.textSoft,
              transition:    'background 0.18s, border-color 0.18s, color 0.18s',
            }}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Issue search ──────────────────────────────────────────────────────────────
// 259 published issues render 24 at a time, so reaching a specific venue meant
// pressing "Load More" ten times. Every published issue is already fetched in one
// payload (no images in it), so filtering is a pure client-side pass: instant, no
// extra round trip, and it composes with the existing windowing.
//
// Matches title AND intro because they carry different things — the title is the
// venue ("Alila Ubud"), the intro carries the destination ("a featured wedding
// venue in Ubud"). Searching both means "Ubud" and "Tuscany" work as well as a
// venue name.

// Diacritic-insensitive so "loviu" finds "Loviù" and "aquila" finds "dell'Aquila".
// Curly apostrophes are folded to straight ones for the same reason: a title
// stored as "Valle dell’Aquila" must match a keyboard-typed "dell'aquila".
function normalise(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’ʼ]/g, "'")
    .toLowerCase();
}

// Every whitespace-separated term must appear somewhere in the haystack, so
// "ubud alila" and "alila ubud" both match, and each extra word narrows.
function matchesQuery(issue, query) {
  const terms = normalise(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = normalise(
    [issue.title, issue.intro, issue.excerpt, seasonLabel(issue)].filter(Boolean).join(' '),
  );
  return terms.every(term => haystack.includes(term));
}

function IssueSearch({ value, onChange, resultCount, totalCount, isMobile, P }) {
  const [focused, setFocused] = useState(false);
  const active = value.trim().length > 0;

  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        maxWidth:     isMobile ? '100%' : 460,
        padding:      '13px 16px',
        background:   P.inputBg,
        border:       `1px solid ${focused ? P.gold : P.hairline}`,
        borderRadius: 20,
        transition:   'border-color 0.2s',
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
             stroke={focused || active ? P.gold : P.textFaint}
             strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, transition: 'stroke 0.2s' }}>
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>

        <input
          className="lwd-issue-search"
          type="search"
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Search by venue or destination"
          aria-label="Search publications by venue or destination"
          style={{
            flex:          1,
            minWidth:      0,
            background:    'transparent',
            border:        'none',
            outline:       'none',
            color:         P.text,
            fontFamily:    NU,
            fontSize:      14,
            letterSpacing: '0.02em',
            // Safari renders its own clear affordance on type=search; we provide our own.
            WebkitAppearance: 'none',
            // ::placeholder can only be reached from CSS, so the theme value is
            // handed to the stylesheet rule as a custom property.
            '--lwd-pub-placeholder': P.textFaint,
          }}
        />

        {active && (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="Clear search"
            style={{
              background: 'none',
              border:     'none',
              color:      P.textFaint,
              cursor:     'pointer',
              padding:    4,
              lineHeight: 1,
              fontSize:   16,
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = P.gold; }}
            onMouseLeave={e => { e.currentTarget.style.color = P.textFaint; }}
          >
            ×
          </button>
        )}
      </div>

      {/* Announced politely so a screen reader hears the count change without
          interrupting typing. Reserved height keeps the grid from jumping. */}
      <div
        aria-live="polite"
        style={{
          minHeight:     18,
          marginTop:     10,
          fontFamily:    NU,
          fontSize:      12,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color:         P.textFaint,
        }}
      >
        {active
          ? `${resultCount} of ${totalCount} ${totalCount === 1 ? 'issue' : 'issues'}`
          : ''}
      </div>
    </div>
  );
}

// ── Footer strip ──────────────────────────────────────────────────────────────
function FooterStrip({ P }) {
  return (
    <div style={{
      background:  P.bg,
      height:      60,
      display:     'flex',
      alignItems:  'center',
      justifyContent: 'center',
      gap:         10,
      padding:     '0 32px',
      borderTop:   `1px solid ${P.cardEdge}`,
    }}>
      <span style={{
        fontFamily:    NU,
        fontSize:      11,
        color:         P.textFaint,
        letterSpacing: '0.06em',
      }}>
        Luxury Wedding Directory · The Magazine
      </span>
      <span style={{
        fontFamily:    NU,
        fontSize:      11,
        color:         P.textFaint,
      }}>
        · © 2026
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PublicationsPage({ onRead, onBack, footerNav, onToggleDark, darkMode, onVendorLogin, onNavigateStandard, onNavigateAbout }) {
  const [issues, setIssues]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  // Prerender readiness (P1b follow-up, 2026-07-24): the hero H1 prop is already
  // explicitly `loading ? null : featured` below — this is the exact settle signal the
  // page's own render logic already uses.
  usePrerenderReady(!loading);
  // Render-only windowing: all published issues are still fetched in one call
  // (cheap, no images in that payload) and the full list still feeds the
  // JSON-LD schema below for SEO, but only the first ISSUES_PAGE_SIZE cards
  // are rendered as <img> tags at once. "Load More" reveals more from the
  // already-fetched list, no extra network round trip needed.
  const ISSUES_PAGE_SIZE = 24;
  const [visibleCount, setVisibleCount] = useState(ISSUES_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [brandTab, setBrandTab] = useState('directory');
  const [countryFilter, setCountryFilter] = useState('All');

  // Light mode reads as a magazine: dark hero, cream inside-pages. Dark mode is
  // unchanged from what shipped.
  const P = paletteFor(darkMode !== false);

  // Brand-scoped first: every downstream filter (country, search, hero,
  // "Load More" windowing) operates within the active tab, never across it.
  const brandIssues = issues.filter(i => (i.brand || 'directory') === brandTab);

  // `listings.country` is inconsistent at the source (casing AND real duplicate
  // spellings like "usa" / "United States"); normalizeCountry collapses both
  // into one canonical key + label for display and de-duplication, without
  // touching the underlying data.
  const directoryCountries = Array.from(
    new Map(
      issues
        .filter(i => (i.brand || 'directory') === 'directory' && i.country)
        .map(i => { const { key, label } = normalizeCountry(i.country); return [key, label]; })
    ).values()
  ).sort();

  const countryScoped = (brandTab === 'directory' && countryFilter !== 'All')
    ? brandIssues.filter(i => i.country && normalizeCountry(i.country).label === countryFilter)
    : brandIssues;

  // Filtering runs over the full fetched (brand + country scoped) list, not the
  // visible window, so a match on issue 200 surfaces without the reader paging
  // to it first.
  const filteredIssues = search.trim()
    ? countryScoped.filter(i => matchesQuery(i, search))
    : countryScoped;

  const brandCounts = {
    directory: issues.filter(i => (i.brand || 'directory') === 'directory').length,
    magazine:  issues.filter(i => (i.brand || 'directory') === 'magazine').length,
  };

  const onBrandChange = (key) => {
    setBrandTab(key);
    setCountryFilter('All');
    setVisibleCount(ISSUES_PAGE_SIZE);
  };

  const onCountryChange = (value) => {
    setCountryFilter(value);
    setVisibleCount(ISSUES_PAGE_SIZE);
  };

  // A new query must restart the window: otherwise a search run after several
  // "Load More" presses would show a stale, over-long slice of the new results.
  const onSearchChange = (value) => {
    setSearch(value);
    setVisibleCount(ISSUES_PAGE_SIZE);
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    setIsMobile(mq.matches);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Inject structured data for the magazine collection
  useEffect(() => {
    if (!issues.length) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Luxury Wedding Directory, The Magazine",
      "description": "Immersive editorial, curated for couples planning exceptional celebrations.",
      "url": "https://luxuryweddingdirectory.com/publications",
      "publisher": {
        "@type": "Organization",
        "name": "Luxury Wedding Directory",
        "url": "https://luxuryweddingdirectory.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://luxuryweddingdirectory.com/logo.png"
        }
      },
      "hasPart": issues.map(issue => ({
        "@type": "PublicationIssue",
        "name": issue.title || "Luxury Wedding Directory Magazine",
        "url": `https://luxuryweddingdirectory.com/publications/${issue.slug}`,
        "issueNumber": issue.issue_number?.toString(),
        "datePublished": issue.published_at,
        "image": issue.cover_image || undefined,
        "description": issue.intro || issue.excerpt || undefined,
      })).filter(Boolean),
    };

    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "publications-schema";
    el.textContent = JSON.stringify(schema);
    // Remove old if exists
    document.getElementById("publications-schema")?.remove();
    document.head.appendChild(el);

    return () => {
      document.getElementById("publications-schema")?.remove();
    };
  }, [issues]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchIssues().then(async ({ data, error: err }) => {
      if (cancelled) return;
      if (err) {
        setError('Failed to load issues.');
        setLoading(false);
        return;
      }
      const published = (data || [])
        .filter(i => i.status === 'published')
        .sort((a, b) => {
          // Sort by most recently published first, `id` unique tiebreak → total order
          const da = new Date(a.published_at || a.created_at || 0);
          const db = new Date(b.published_at || b.created_at || 0);
          return (db - da) || String(a.id).localeCompare(String(b.id));
        });

      // Fallback: for issues without a cover_image, use page 1's image_url.
      // This fixes older published issues that were published before cover
      // auto-population was added, no re-publish required.
      const needsFallback = published.filter(i => !i.cover_image).map(i => i.id);
      if (needsFallback.length > 0) {
        const { data: firstPages } = await supabase
          .from('magazine_issue_pages')
          .select('issue_id, image_url, thumbnail_url')
          .in('issue_id', needsFallback)
          .eq('page_number', 1);
        const coverByIssue = Object.fromEntries(
          (firstPages || []).map(p => [p.issue_id, p.image_url || p.thumbnail_url])
        );
        published.forEach(i => {
          if (!i.cover_image && coverByIssue[i.id]) {
            i.cover_image = coverByIssue[i.id];
          }
        });
      }

      // Country, for the Directory country filter. magazine_issues carries no
      // listing FK — directory-brand issues are 1:1 with a listing by slug
      // (the same convention used elsewhere in this codebase), so a slug join
      // borrows `country` from there rather than adding new schema for it.
      const directorySlugs = published
        .filter(i => (i.brand || 'directory') === 'directory')
        .map(i => i.slug);
      if (directorySlugs.length > 0) {
        const { data: matchedListings } = await supabase
          .from('listings')
          .select('slug, country')
          .in('slug', directorySlugs);
        const countryBySlug = Object.fromEntries(
          (matchedListings || []).map(l => [l.slug, l.country]).filter(([, c]) => c)
        );
        published.forEach(i => {
          if (countryBySlug[i.slug]) i.country = countryBySlug[i.slug];
        });
      }

      if (cancelled) return;
      setIssues(published);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const handleRead = (slug) => {
    if (onRead) onRead(slug);
  };

  // Sticky hero: a publication flagged is_featured stays pinned at the top of the page
  // even as newer issues publish. Falls back to the most recent published issue.
  // Scoped to the active brand tab so the hero always matches what's below it
  // (brandIssues is already sorted newest-first, so among multiple featured the newest wins).
  const featured = brandIssues.find(i => i.is_featured) || brandIssues[0] || null;

  return (
    <>
    <Helmet>
      <title>The Magazine | Luxury Wedding Directory</title>
      <meta name="description" content="Discover the Luxury Wedding Directory editorial magazine, inspiring couples planning exceptional weddings with exclusive venues, fashion, and destination features." />
      <link rel="canonical" href="https://luxuryweddingdirectory.com/publications" />
      <meta property="og:title"       content="LWD, The Magazine" />
      <meta property="og:description" content="Luxury editorial magazine for discerning couples planning exceptional weddings." />
      <meta property="og:type"        content="website" />
      <meta property="og:url"         content="https://luxuryweddingdirectory.com/publications" />
      {/* RSS autodiscovery, lets feed readers and Google Discover find the feed */}
      <link rel="alternate" type="application/rss+xml"
            title="LWD, The Magazine"
            href="https://luxuryweddingdirectory.com/magazine-feed.xml" />
    </Helmet>
    <HomeNav
      onToggleDark={onToggleDark}
      darkMode={darkMode}
      onVendorLogin={onVendorLogin}
      onNavigateStandard={onNavigateStandard}
      onNavigateAbout={onNavigateAbout}
    />
    <div style={{ background: P.bg, minHeight: '100vh', color: P.text, fontFamily: NU }}>
      {/* Fixed back link */}
      <BackLink onBack={onBack} P={P} />

      {/* Cinematic hero */}
      <Hero
        featured={loading ? null : featured}
        onRead={handleRead}
        isMobile={isMobile}
      />

      {/* All Issues section */}
      <section id="all-issues" style={{ padding: '80px 0 40px' }}>
        <div style={{
          maxWidth: 1440,
          margin:   '0 auto',
          padding:  isMobile ? '0 20px' : '0 32px',
        }}>
          <SectionHeader title="All Issues" P={P} />

          {!loading && !error && issues.length > 0 && (
            <BrandTabs active={brandTab} onChange={onBrandChange} counts={brandCounts} P={P} />
          )}

          {!loading && !error && brandTab === 'directory' && (
            <CountryFilter
              countries={directoryCountries}
              active={countryFilter}
              onChange={onCountryChange}
              isMobile={isMobile}
              P={P}
            />
          )}

          {/* Only offered once there is something to search. Hidden while loading
              and on the error/empty states so it never sits above nothing. */}
          {!loading && !error && issues.length > 0 && (
            <IssueSearch
              value={search}
              onChange={onSearchChange}
              resultCount={filteredIssues.length}
              totalCount={countryScoped.length}
              isMobile={isMobile}
              P={P}
            />
          )}

          {/* Archive grid: 4 covers per row on desktop, 3 on tablet, 1-2 on mobile.
              Fewer columns inside the 1280 container = larger, more collectible
              covers. Spacing (the 28px gap) is unchanged, the extra width grows
              the covers, it does not pack more in. */}
          <style>{`
            .lwd-issues-grid { display: grid; gap: 28px; grid-template-columns: repeat(4, 1fr); }
            @media (max-width: 1023px) and (min-width: 768px) { .lwd-issues-grid { grid-template-columns: repeat(3, 1fr); } }
            @media (max-width: 767px) { .lwd-issues-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); } }
            /* Chrome/Safari draw their own clear button on type=search, which would
               sit next to ours. Suppressed here rather than inline: it is a
               pseudo-element and cannot be reached from a style prop. type=search
               is kept for the semantics and the Escape-to-clear behaviour. */
            .lwd-issue-search::-webkit-search-cancel-button,
            .lwd-issue-search::-webkit-search-decoration { -webkit-appearance: none; appearance: none; }
            .lwd-issue-search::placeholder { color: var(--lwd-pub-placeholder, rgba(255,255,255,0.35)); }
          `}</style>

          {/* Loading skeletons */}
          {loading && (
            <div className="lwd-issues-grid">
              {[1, 2, 3, 4].map(n => <SkeletonCard key={n} P={P} />)}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div style={{
              textAlign:  'center',
              padding:    '60px 0',
              fontFamily: NU,
              fontSize:   14,
              color:      'rgba(255,100,100,0.75)',
            }}>
              {error}
            </div>
          )}

          {/* Empty */}
          {!loading && !error && issues.length === 0 && (
            <div style={{
              display:        'flex',
              flexDirection:  'column',
              alignItems:     'center',
              justifyContent: 'center',
              padding:        '80px 24px',
              textAlign:      'center',
            }}>
              <div style={{ fontSize: 40, marginBottom: 20, opacity: 0.2, color: P.gold }}>◈</div>
              <h2 style={{
                fontFamily: GD,
                fontSize:   26,
                fontWeight: 300,
                fontStyle:  'italic',
                color:      P.text,
                margin:     '0 0 12px',
              }}>
                No Issues Published Yet
              </h2>
              <p style={{
                fontFamily: NU,
                fontSize:   14,
                color:      P.textFaint,
                maxWidth:   340,
                lineHeight: 1.6,
              }}>
                The latest edition of Luxury Wedding Directory magazine will appear here when published.
              </p>
            </div>
          )}

          {/* Grid, only the first ISSUES_PAGE_SIZE covers are rendered (and thus
              requested) at once; "Load More" reveals more from the already-fetched
              `issues` array. */}
          {/* Nothing in the current tab/country/search combination. Distinct from
              the "nothing published anywhere" state above — here the fix is a
              different query or filter, not waiting. */}
          {!loading && !error && issues.length > 0 && filteredIssues.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 24px 80px' }}>
              <div style={{ fontSize: 32, marginBottom: 18, opacity: 0.2, color: P.gold }}>◈</div>
              <h3 style={{
                fontFamily: GD,
                fontSize:   24,
                fontWeight: 300,
                fontStyle:  'italic',
                color:      P.text,
                margin:     '0 0 10px',
              }}>
                {search.trim() ? `No issues match "${search.trim()}"` : 'Nothing here yet'}
              </h3>
              <p style={{
                fontFamily: NU,
                fontSize:   13,
                color:      P.textFaint,
                margin:     '0 0 24px',
              }}>
                {search.trim()
                  ? 'Try a venue name or a destination.'
                  : countryFilter !== 'All'
                    ? `No ${brandTab === 'magazine' ? 'magazine' : 'directory'} issues from ${countryFilter} yet.`
                    : `No ${brandTab === 'magazine' ? 'magazine' : 'directory'} issues published yet.`}
              </p>
              {(search.trim() || countryFilter !== 'All') && (
                <button
                  type="button"
                  onClick={() => { onSearchChange(''); onCountryChange('All'); }}
                  style={{
                    padding:       '11px 28px',
                    background:    'transparent',
                    border:        `1px solid ${P.gold}`,
                    color:         P.gold,
                    fontFamily:    NU,
                    fontSize:      11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    cursor:        'pointer',
                    transition:    'background 0.2s, color 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = P.gold; e.currentTarget.style.color = P.onGold; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.gold; }}
                >
                  Show all issues
                </button>
              )}
            </div>
          )}

          {!loading && !error && filteredIssues.length > 0 && (
            <>
              <div className="lwd-issues-grid" style={{ paddingBottom: 40 }}>
                {filteredIssues.slice(0, visibleCount).map((issue, idx) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    onRead={handleRead}
                    // The "Latest" ribbon belongs to the newest published issue, not
                    // to whatever happens to sort first inside a filtered set.
                    isLatest={!search.trim() && idx === 0}
                    P={P}
                  />
                ))}
              </div>

              {visibleCount < filteredIssues.length && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 60 }}>
                  <button
                    onClick={() => setVisibleCount(v => v + ISSUES_PAGE_SIZE)}
                    style={{
                      padding:       '14px 36px',
                      background:    'transparent',
                      border:        `1px solid ${P.gold}`,
                      color:         P.gold,
                      fontFamily:    NU,
                      fontSize:      12,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      cursor:        'pointer',
                      transition:    'background 0.2s, color 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = P.gold; e.currentTarget.style.color = P.onGold; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.gold; }}
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Footer strip */}
      <FooterStrip P={P} />
    </div>
    </>
  );
}
